import React, { useMemo } from "react";
import { Row, Col, Space } from "antd";
import color from "../../../resource/color";
import Text from "../../../components/Text/Text";
import StatusBadge from "../../../components/Badge/StatusBadge";
import type { CreditApproversEntity } from "../../../entities/CreditApproversEntity";
import type { SoApproversEntity } from "../../../entities/SoApproversEntity";
import { normalizeStatus, STATUS_CREDIT } from "../../../definitions/creditStatus";
import { dateFormatter } from "../../../utility/Formatter";
import type { OrderEntity } from "../../../entities/OrderEntity";

type ApproverType = "specialRequest" | "so" | "credit";
type ApproverItem = CreditApproversEntity | SoApproversEntity;

interface EvidenceSectionProps {
  data: ApproverItem[];
  orderData?: OrderEntity & {
    signImageUrl?: string;
    specialRequestApprover?: string;
    hasSpecialRequest?: boolean;
  };
  title?: string;
  type?: ApproverType;
  otherApprovers?: ApproverItem[];
}

const COLUMNS_PER_ROW = 3;
const SIGNATURE_PLACEHOLDER_HEIGHT = "90px";
const REASON_SECTION_HEIGHT = "80px";

function EvidenceSection({ data, title, type, orderData, otherApprovers }: EvidenceSectionProps) {
  const groupedBySequence = useMemo(() => {
    if (type === "specialRequest") {
      return {};
    }

    return data.reduce(
      (acc, item) => {
        const sequence = item.sequence || 0;
        if (!acc[sequence]) {
          acc[sequence] = [];
        }
        acc[sequence].push(item);
        return acc;
      },
      {} as Record<number, ApproverItem[]>,
    );
  }, [data, type]);

  const sequences = useMemo(() => {
    if (type === "specialRequest") {
      return [];
    }
    return Object.keys(groupedBySequence)
      .map(Number)
      .sort((a, b) => a - b);
  }, [groupedBySequence, type]);

  const hasAnyRejected = useMemo(() => {
    const dataHasRejected = data.some((item) => item.action === "REJECT");
    const otherHasRejected = otherApprovers?.some((item) => item.action === "REJECT");
    const creditHasRejected = orderData?.orderCreditApprovers?.some(
      (item) => item.action === "REJECT",
    );
    const soHasRejected = orderData?.orderSoApprovers?.some((item) => item.action === "REJECT");
    const specialRequestRejected =
      orderData?.hasSpecialRequest && orderData?.status === "REJECT_ORDER";
    const othersRejected = [
      "SHOPAPP_CANCEL_ORDER",
      "COMPANY_CANCEL_ORDER",
      "REJECT_ORDER",
      "EXPIRED",
    ].includes(orderData?.status || "");

    return (
      dataHasRejected ||
      otherHasRejected ||
      creditHasRejected ||
      soHasRejected ||
      specialRequestRejected ||
      othersRejected
    );
  }, [data, otherApprovers, orderData]);

  const currentStatus = useMemo(() => {
    if (!orderData) {
      return { label: "รออนุมัติ", bgColor: color.warning };
    }

    const { status, specialRequestApprover } = orderData;

    if (status === "WAIT_APPROVE_ORDER") {
      return { label: "รออนุมัติ", bgColor: color.warning };
    }

    if (status === "REJECT_ORDER") {
      return { label: "ไม่อนุมัติ", bgColor: color.error };
    }

    const statusStr = status as string;
    if (statusStr === "EXPIRED" || statusStr === "SALE_CANCEL_ORDER") {
      if (!specialRequestApprover) {
        return { label: "รออนุมัติ", bgColor: color.warning };
      }
      return { label: "อนุมัติแล้ว", bgColor: color.success };
    }

    return { label: "อนุมัติแล้ว", bgColor: color.success };
  }, [orderData]);

  const calculateColSpan = (itemCount: number): number => {
    if (itemCount === 1) return 24;
    if (itemCount === 2) return 12;
    return 8;
  };

  const getStatusColor = (status: string): string => {
    switch (status) {
      case "PENDING":
        return color.warning;
      case "APPROVED":
        return color.success;
      default:
        return color.error;
    }
  };

  const shouldShowBorderRight = (index: number, isLastItemInRow: boolean): boolean => {
    return !isLastItemInRow;
  };

  // เพิ่มฟังก์ชันสำหรับ border bottom
  const shouldShowBorderBottom = (index: number, itemCount: number): boolean => {
    // จำนวน row ทั้งหมด
    const totalRows = Math.ceil(itemCount / COLUMNS_PER_ROW);
    // row ปัจจุบัน (เริ่มที่ 0)
    const currentRow = Math.floor(index / COLUMNS_PER_ROW);
    // ถ้าเป็นแถวสุดท้าย ไม่ต้องแสดง border bottom
    return currentRow < totalRows - 1;
  };

  const renderSignature = (signImageUrl?: string, isPending?: boolean) => {
    if (signImageUrl && !isPending) {
      return (
        <img
          src={signImageUrl}
          alt='signature'
          style={{ width: "150px", height: "80px", objectFit: "contain" }}
        />
      );
    }
    return <div style={{ margin: SIGNATURE_PLACEHOLDER_HEIGHT }} />;
  };

  const renderReasonSection = (action: string, reason?: string) => {
    if (action === "PENDING") {
      return null;
    }

    return (
      <div
        style={{
          width: "100%",
          height: REASON_SECTION_HEIGHT,
          padding: "20px",
          display: "flex",
          borderRadius: "0px 0px 10px 10px",
          borderTop: "1px solid #E8E8E8",
          backgroundColor: "#F3F6F9",
          border: "1px solid #E8E8E8",
        }}
      >
        <div>
          <Text fontWeight={700}>เหตุผล</Text>
          <br />
          <Text fontFamily='Sarabun' fontWeight={400}>
            {reason || "-"}
          </Text>
        </div>
      </div>
    );
  };

  const renderSpecialRequestSection = () => {
    if (!orderData || type !== "specialRequest") {
      return null;
    }

    const isApprovedOrRejected =
      currentStatus.label === "ไม่อนุมัติ" || currentStatus.label === "อนุมัติแล้ว";

    // Check if this specialRequest should show auto-rejected
    // (when other approvers rejected but specialRequest was approved or pending)
    const isSpecialRequestAutoRejected =
      hasAnyRejected && currentStatus.label !== "ไม่อนุมัติ" && orderData.status !== "REJECT_ORDER";

    const displayLabel = isSpecialRequestAutoRejected
      ? "ไม่อนุมัติ (อัตโนมัติ)"
      : currentStatus.label;

    const displayBgColor = isSpecialRequestAutoRejected ? color.error : currentStatus.bgColor;

    return (
      <>
        <Row
          style={{
            border: "1px solid #E8E8E8",
            borderRadius: "10px",
            backgroundColor: "#fff",
            marginTop: "18px",
          }}
        >
          <Col span={24}>
            <div
              style={{
                height: "100%",
                width: "100%",
                padding: "20px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                borderRight: "1px solid #E8E8E8",
                borderBottom: "1px solid #E8E8E8",
              }}
            >
              <Space
                direction='vertical'
                size={4}
                style={{ display: "flex", alignItems: "center", justifyContent: "center" }}
              >
                <Text fontFamily='Sarabun' fontWeight={700}>
                  ผู้อนุมัติ
                </Text>
                {renderSignature(orderData.signImageUrl, currentStatus.label !== "อนุมัติแล้ว")}

                <Space
                  direction='vertical'
                  size={1}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  {isApprovedOrRejected && (
                    <Text fontFamily='Sarabun'>{orderData.specialRequestApprover || ""}</Text>
                  )}

                  <Text fontFamily='Sarabun'>SALE MANAGER</Text>
                  <Space size={4}>
                    {isSpecialRequestAutoRejected && currentStatus.label === "อนุมัติแล้ว" && (
                      <StatusBadge
                        label={currentStatus.label}
                        bgColor={currentStatus.bgColor}
                        borderRadius='12px'
                      />
                    )}
                    <StatusBadge
                      label={displayLabel}
                      bgColor={displayBgColor}
                      borderRadius='12px'
                    />
                  </Space>
                  {(isApprovedOrRejected || isSpecialRequestAutoRejected) && orderData.updateAt && (
                    <Text fontFamily='Sarabun'>{dateFormatter(orderData.updateAt, true)}</Text>
                  )}
                </Space>
              </Space>
            </div>
          </Col>
        </Row>
        {orderData.status === "REJECT_ORDER" && (
          <div
            style={{
              border: "1px solid #F3F6F9",
              borderRadius: "0px 0px 10px 10px",
              backgroundColor: "#F3F6F9",
              width: "100%",
              padding: "22px",
            }}
          >
            <div>
              <Text fontFamily='Sarabun' fontWeight={700}>
                เหตุผล :
              </Text>
              <br />
              <Text fontFamily='Sarabun' fontWeight={400}>
                {orderData.cancelRemark || "-"}
              </Text>
            </div>
          </div>
        )}
      </>
    );
  };

  return (
    <div style={{ margin: "28px 0px" }}>
      {title && <Text fontWeight={700}>{title}</Text>}
      {renderSpecialRequestSection()}
      {sequences.map((sequence) => {
        const sequenceItems = groupedBySequence[sequence] || [];
        const seqRequired = sequenceItems[0]?.sequenceRequired ?? sequenceItems.length;
        const nonPending = sequenceItems.filter((a) => a.action !== "PENDING");
        const remainingPending = sequenceItems
          .filter((a) => a.action === "PENDING")
          .slice(0, seqRequired - nonPending.length);
        const filteredSequenceItems = [...nonPending, ...remainingPending].filter(
          (v, i, arr) =>
            arr.findIndex(
              (x) =>
                (x as CreditApproversEntity).orderCreditApproverId ===
                (v as CreditApproversEntity).orderCreditApproverId,
            ) === i,
        );
        const colSpan = calculateColSpan(filteredSequenceItems.length);

        return (
          <div key={sequence} style={{ margin: "18px 0 0 0" }}>
            <Row
              style={{
                border: "1px solid #E8E8E8",
                borderRadius: "10px",
                backgroundColor: "#fff",
              }}
            >
              {filteredSequenceItems.map((item: any, index) => {
                const normalizedStatus = normalizeStatus(item.action);
                const isCurrentItemRejected = item.action === "REJECT";
                const displayStatus =
                  hasAnyRejected && !isCurrentItemRejected ? "AUTO_REJECTED" : normalizedStatus;

                const isAutoRejected = displayStatus === "AUTO_REJECTED";
                const isLastItemInRow = index % COLUMNS_PER_ROW === COLUMNS_PER_ROW - 1;
                const showBorderRight = shouldShowBorderRight(index, isLastItemInRow);
                const showBorderBottom = shouldShowBorderBottom(
                  index,
                  filteredSequenceItems.length,
                );
                const getApproverKey = (item: ApproverItem) =>
                  (item as any).orderCreditApproverId ||
                  (item as any).orderSoApproverId ||
                  (item as any).id;
                return (
                  <Col span={colSpan} key={getApproverKey(item)} style={{ position: "relative" }}>
                    <div
                      style={{
                        height: "100%",
                        width: "100%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        padding: "22px",
                        borderBottom: showBorderBottom ? "1px solid #E8E8E8" : "none",
                        borderRight: showBorderRight ? "1px solid #E8E8E8" : "none",
                        paddingBottom: item.action !== "PENDING" ? REASON_SECTION_HEIGHT : "20px",
                      }}
                    >
                      <Space
                        direction='vertical'
                        size={1}
                        style={{ display: "flex", alignItems: "center", justifyContent: "center" }}
                      >
                        <Text fontFamily='Sarabun' fontWeight={700}>
                          {type === "so" ? `ผู้อนุมัติ` : `ผู้อนุมัติลำดับที่ ${sequence}`}
                        </Text>

                        {filteredSequenceItems.length > 1 && type !== "so" && (
                          <Text fontFamily='Sarabun' fontWeight={700} fontSize={14}>
                            {`(คนที่ ${index + 1})`}
                          </Text>
                        )}

                        {renderSignature(item.signImageUrl, item.action === "PENDING")}

                        <Space
                          direction='vertical'
                          size={1}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            paddingBottom: "12px",
                          }}
                        >
                          {item.action !== "PENDING" && (
                            <Text fontFamily='Sarabun'>{item.userStaffName || ""}</Text>
                          )}
                          <Text fontFamily='Sarabun'>{item.role || ""}</Text>

                          <Space size={4}>
                            <StatusBadge
                              label={
                                isAutoRejected && normalizedStatus !== "APPROVED"
                                  ? "ไม่อนุมัติ (อัตโนมัติ)"
                                  : STATUS_CREDIT[normalizedStatus].label
                              }
                              bgColor={
                                isAutoRejected && normalizedStatus !== "APPROVED"
                                  ? getStatusColor("REJECTED")
                                  : getStatusColor(normalizedStatus)
                              }
                              borderRadius='12px'
                            />
                            {isAutoRejected && normalizedStatus === "APPROVED" && (
                              <StatusBadge
                                label='ไม่อนุมัติ (อัตโนมัติ)'
                                bgColor={getStatusColor("REJECTED")}
                                borderRadius='12px'
                              />
                            )}
                          </Space>

                          {(normalizedStatus !== "PENDING" || isAutoRejected) && (
                            <Text fontFamily='Sarabun'>
                              {dateFormatter(item.specialRequestUpdatedAt, true)}
                            </Text>
                          )}
                        </Space>
                      </Space>
                    </div>
                    <div style={{ position: "absolute", bottom: -2, left: -1, right: -1 }}>
                      {renderReasonSection(item.action, item.reason)}
                    </div>
                  </Col>
                );
              })}
            </Row>
          </div>
        );
      })}
    </div>
  );
}

export default EvidenceSection;
